import { useEffect, useState } from "react";
import { Accordion } from "flowbite-react";
import { AccordionCard } from "./draggableCard";
import update from "immutability-helper";

export const DraggableAccordion = ({
  accordionData,
  formState,
  setFormState,
  dataProperty,
  handleInputChange,
  formFields,
}) => {
  // Only the drag-reordered section ORDER is persisted across opens - each
  // section's `content` is always rebuilt fresh from the live accordionData
  // prop below, so a tenant's property-visibility selection is never masked
  // by a stale cached field list from a previously opened record.
  const applyCachedOrder = (freshSections) => {
    let cachedOrder;
    try {
      cachedOrder = JSON.parse(localStorage.getItem("accordionOrder"));
    } catch {
      cachedOrder = null;
    }
    if (!Array.isArray(cachedOrder) || cachedOrder.length === 0) return freshSections;

    const byId = new Map(freshSections.map((section) => [section.id, section]));
    const ordered = cachedOrder
      .map((id) => byId.get(id))
      .filter(Boolean);
    freshSections.forEach((section) => {
      if (!cachedOrder.includes(section.id)) ordered.push(section);
    });
    return ordered;
  };

  const [cards, setCards] = useState(() => applyCachedOrder(accordionData));

  useEffect(() => {
    setCards(applyCachedOrder(accordionData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accordionData]);

  const visibleCards = cards?.filter((card) => card?.content?.length > 0) || [];

  // dragIndex/hoverIndex come from AccordionCard, which only ever sees the
  // rendered (visible) list below - reorder within that list, then splice
  // hidden cards back into their nearest visible neighbor's position in the
  // full `cards` array so an empty section doesn't lose its place once a
  // property-visibility change makes it visible again later.
  const moveCard = (dragIndex, hoverIndex) => {
    setCards((prevCards) => {
      const visible = prevCards.filter((card) => card?.content?.length > 0);
      const reorderedVisible = update(visible, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, visible[dragIndex]],
        ],
      });
      const hidden = prevCards.filter((card) => !(card?.content?.length > 0));
      const next = [...reorderedVisible, ...hidden];
      localStorage.setItem("accordionOrder", JSON.stringify(next.map((section) => section.id)));
      return next;
    });
  };

  return (
    <Accordion className="border-none" collapseAll>
      {visibleCards.map((card, index) => (
        <AccordionCard
          key={card.id}
          id={card.id}
          index={index}
          formState={formState}
          setFormState={setFormState}
          data={card}
          moveCard={moveCard}
          handleInputChange={handleInputChange}
          dataProperty={dataProperty}
        />
      ))}
    </Accordion>
  );
};
